import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, Eye, Lightbulb, Shuffle, HelpCircle } from 'lucide-react';
import { cn } from './ClueCardSimple';

const ByrdleClueCard = ({ clue, date, isToday, puzzleNumber, puzzleTitle }) => {
  const [activeHint, setActiveHint] = useState(null);
  const [isAnswerRevealed, setIsAnswerRevealed] = useState(false);

  const toggleHint = (key) => {
    setActiveHint(activeHint === key ? null : key);
  };

  const answerLength = clue.answer.length;
  const tiles = Array.from({ length: answerLength });

  const HintItem = ({ label, subLabel, icon: Icon, content, isOpen, onClick }) => (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white mb-6">
      <div className="p-4 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-brand-blue/10 text-brand-blue">
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <span className="block font-bold text-gray-900">{label}</span>
            <span className="block text-xs text-gray-500 font-medium uppercase tracking-wide">{subLabel}</span>
          </div>
        </div>
      </div>
      <div className="p-6 relative min-h-[100px] flex items-center justify-center">
        {isOpen ? (
          <div className="text-lg text-gray-700 leading-relaxed text-center animate-in fade-in duration-300">
            {content}
          </div>
        ) : (
          <>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-full h-full bg-gray-100/50 backdrop-blur-md absolute inset-0 z-10"></div>
              <button 
                onClick={onClick}
                className="z-20 flex items-center gap-2 px-6 py-3 bg-brand-blue text-white rounded-full font-bold shadow-lg hover:bg-blue-600 transition-all hover:scale-105"
              >
                <Eye className="w-4 h-4" />
                Unlock Hint
              </button>
            </div>
            <div className="text-lg text-gray-400 leading-relaxed text-center filter blur-sm select-none">
              {content.replace(/[a-zA-Z]/g, 'x')}
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-2xl border-2 border-gray-100 p-6 md:p-8 shadow-card mb-8"
    >
       <div className="flex items-center gap-3 mb-6">
         <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-gray-100 text-gray-600">
           Clue
         </span>
         {date && (
            <span className="text-gray-400 text-sm font-bold">
              {new Date(date).toLocaleDateString()}
            </span>
         )}
         {puzzleNumber && (
             <span className="text-gray-400 text-sm font-bold">Byrdle #{puzzleNumber}</span>
         )}
       </div>

       <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-8 leading-tight">
         {puzzleTitle || "Daily Byrdle"}
       </h1>

       {/* Tiles */}
       <div className="flex flex-wrap gap-2 mb-10">
         {tiles.map((_, index) => (
           <div 
             key={index}
             className={cn(
               "w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-lg border-b-4 border-x border-t text-2xl md:text-3xl font-bold transition-all duration-300",
               isAnswerRevealed 
                 ? "bg-brand-blue border-brand-blue border-b-blue-700 text-white shadow-lg transform translate-y-[2px] border-b-[2px]" 
                 : "bg-white border-gray-200 border-b-gray-300 text-gray-300"
             )}
           >
             {isAnswerRevealed ? (
               <motion.span
                 initial={{ opacity: 0, scale: 0.5 }}
                 animate={{ opacity: 1, scale: 1 }}
                 transition={{ delay: index * 0.05 }}
               >
                 {clue.answer[index]}
               </motion.span>
             ) : (
               <span className="opacity-10">?</span>
             )}
           </div>
         ))}
       </div>

       {/* Action Buttons */}
       <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
         <button
           onClick={() => setIsAnswerRevealed(true)}
           disabled={isAnswerRevealed}
           className={cn(
             "flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-lg font-bold transition-all shadow-sm border-2",
             isAnswerRevealed
               ? "bg-gray-100 text-gray-400 border-gray-100 cursor-default"
               : "bg-gray-900 text-white border-gray-900 hover:bg-black hover:shadow-md"
           )}
         >
           <Eye className="w-5 h-5" />
           Reveal Answer
         </button>
       </div>

       {/* Hints Section */}
       <div className="space-y-4">
         <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">Progressive Hints</h2>
         
         {clue.hint_1 && (
           <HintItem 
             label="Hint 1" 
             subLabel="First Nudge"
             icon={Lightbulb}
             content={clue.hint_1}
             isOpen={activeHint === 'hint1'}
             onClick={() => toggleHint('hint1')}
           />
         )}
         
         {clue.hint_2 && (
           <HintItem 
             label="Hint 2" 
             subLabel="Getting Closer"
             icon={Shuffle}
             content={clue.hint_2}
             isOpen={activeHint === 'hint2'}
             onClick={() => toggleHint('hint2')}
           />
         )}
         
         {clue.hint_3 && (
           <HintItem 
             label="Hint 3" 
             subLabel="Almost There"
             icon={HelpCircle}
             content={clue.hint_3}
             isOpen={activeHint === 'hint3'}
             onClick={() => toggleHint('hint3')}
           />
         )}
       </div>
    </motion.div>
  );
};

export default ByrdleClueCard;


















